import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Send, RefreshCw, Eye, Plus, Trash2 } from "lucide-react";
import api from "@/lib/api";

const MODES = [["instant", "Email immédiat"], ["in_app_only", "Centre seulement"], ["disabled", "Désactivé"]];
const STATUS_COLORS = {
  sent: "text-bronze", delivered: "text-green-700", queued: "text-ink-soft",
  error: "text-bordeaux-700", failed: "text-bordeaux-700", bounced: "text-bordeaux-700",
};

export default function CommunicationsPanel() {
  const [cfg, setCfg] = useState(null);
  const [deliveries, setDeliveries] = useState([]);
  const [testTo, setTestTo] = useState("");
  const [newRcpt, setNewRcpt] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [c, d] = await Promise.all([
      api.get("/admin/communications/settings"),
      api.get("/admin/communications/deliveries?limit=60"),
    ]);
    setCfg(c.data); setDeliveries(d.data.items);
    setTestTo((t) => t || (c.data.internal_recipients?.[0] || ""));
  };
  useEffect(() => { load(); }, []);

  const save = async (body) => {
    setBusy(true);
    try { const { data } = await api.put("/admin/communications/settings", body); setCfg({ ...cfg, ...data }); toast.success("Réglages enregistrés"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const sendTest = async () => {
    if (!testTo.includes("@")) { toast.error("Adresse invalide"); return; }
    setBusy(true);
    try { await api.post("/admin/communications/test", { to: testTo, template: "appointment.confirmed" }); toast.success("Email de test envoyé"); await load(); }
    catch { toast.error("Envoi impossible"); }
    finally { setBusy(false); }
  };

  const preview = async (id) => {
    try {
      const { data } = await api.get(`/admin/communications/deliveries/${id}/html`, { responseType: "text" });
      const w = window.open("", "_blank");
      w.document.write(data);
      w.document.close();
    } catch { toast.error("Aperçu indisponible"); }
  };

  const resend = async (id) => {
    try { await api.post(`/email/deliveries/${id}/retry?force=true`); toast.success("Renvoi lancé"); setTimeout(load, 1500); }
    catch { toast.error("Renvoi impossible"); }
  };

  if (!cfg) return <div className="text-sm text-ink-soft">Chargement…</div>;

  return (
    <div className="space-y-8" data-testid="communications-panel">
      <div>
        <h1 className="font-display text-3xl md:text-4xl text-anthracite">Communications</h1>
        <p className="mt-3 text-sm text-ink-soft max-w-2xl leading-relaxed">
          Expéditeur <b>{cfg.sender}</b> · fournisseur {cfg.provider_configured ? "configuré" : "non configuré"}.
          Les emails clients du cycle rendez-vous sont toujours envoyés ; les réglages ci-dessous concernent les
          alertes internes.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-black/10 rounded-rama-md p-6">
          <div className="font-mono-caption text-ink-soft mb-4">Destinataires internes</div>
          <ul className="space-y-2">
            {cfg.internal_recipients.map((r) => (
              <li key={r} className="flex items-center justify-between gap-3 text-sm text-anthracite">
                <span data-testid={`rcpt-${r}`}>{r}</span>
                {cfg.internal_recipients.length > 1 && (
                  <button data-testid={`rcpt-remove-${r}`} onClick={() => save({ internal_recipients: cfg.internal_recipients.filter((x) => x !== r) })}
                    className="text-ink-soft hover:text-bordeaux-700"><Trash2 size={14} /></button>
                )}
              </li>
            ))}
          </ul>
          <div className="mt-4 flex gap-2">
            <input data-testid="rcpt-input" value={newRcpt} onChange={(e) => setNewRcpt(e.target.value)} placeholder="adresse@entreprise.fr"
              className="flex-1 border-b border-black/25 py-2 bg-transparent focus:border-bordeaux-700 outline-none text-sm" />
            <button data-testid="rcpt-add" disabled={busy} onClick={() => { save({ internal_recipients: [...cfg.internal_recipients, newRcpt] }); setNewRcpt(""); }}
              className="btn-ghost text-sm"><Plus size={14} /> Ajouter</button>
          </div>

          <div className="mt-8 font-mono-caption text-ink-soft mb-3">Rappels avant rendez-vous (heures)</div>
          <div className="flex flex-wrap gap-2">
            {[48, 24, 12, 4, 2, 1].map((h) => {
              const on = (cfg.reminder_hours || []).includes(h);
              return (
                <button key={h} data-testid={`reminder-${h}`} disabled={busy}
                  onClick={() => save({ reminder_hours: on ? cfg.reminder_hours.filter((x) => x !== h) : [...cfg.reminder_hours, h].sort((a, b) => b - a) })}
                  className={`px-3.5 py-1.5 rounded-full border text-sm transition-colors ${on ? "bg-bordeaux-700 text-ivory border-bordeaux-700" : "border-black/20 hover:border-anthracite"}`}>
                  H-{h}
                </button>
              );
            })}
          </div>

          <label className="mt-8 flex items-start gap-3 text-sm text-anthracite cursor-pointer">
            <input type="checkbox" data-testid="post-visit-toggle" checked={cfg.post_visit_enabled}
              onChange={(e) => save({ post_visit_enabled: e.target.checked })} className="mt-1 accent-[#6B0E1A]" />
            <span>Envoyer un message de suivi après la visite technique
              <span className="block text-xs text-ink-soft">Désactivé par défaut.</span></span>
          </label>

          <div className="mt-8 pt-6 border-t border-black/10">
            <div className="font-mono-caption text-ink-soft mb-3">Email de test</div>
            <div className="flex gap-2">
              <input data-testid="test-email-input" value={testTo} onChange={(e) => setTestTo(e.target.value)}
                className="flex-1 border-b border-black/25 py-2 bg-transparent focus:border-bordeaux-700 outline-none text-sm" />
              <button data-testid="test-email-send" disabled={busy} onClick={sendTest} className="btn-primary text-sm">
                <Send size={14} /> Envoyer
              </button>
            </div>
            <p className="mt-2 text-xs text-ink-soft">Données fictives clairement signalées, avec fichier calendrier joint.</p>
          </div>
        </div>

        <div className="bg-white border border-black/10 rounded-rama-md p-6">
          <div className="font-mono-caption text-ink-soft mb-4">Alertes internes par événement</div>
          <ul className="divide-y divide-black/5">
            {Object.entries(cfg.events_catalog).map(([key, meta]) => (
              <li key={key} className="py-3 flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-sm text-anthracite">{meta.label}</div>
                  <div className="text-[0.68rem] text-ink-soft">priorité {meta.priority}</div>
                </div>
                <div className="flex gap-1">
                  {MODES.map(([v, l]) => (
                    <button key={v} data-testid={`event-${key}-${v}`} disabled={busy}
                      onClick={() => save({ events: { [key]: { mode: v } } })}
                      className={`px-2.5 py-1 rounded-rama-sm text-xs transition-colors ${
                        (cfg.events[key]?.mode || "instant") === v ? "bg-anthracite text-ivory" : "text-ink-soft hover:bg-black/5"}`}>
                      {l}
                    </button>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="bg-white border border-black/10 rounded-rama-md p-6 overflow-x-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="font-mono-caption text-ink-soft">Journal des envois</div>
          <button data-testid="deliveries-refresh" onClick={load} className="text-xs text-bordeaux-700 hover:underline inline-flex items-center gap-1">
            <RefreshCw size={12} /> Rafraîchir
          </button>
        </div>
        <table className="w-full text-sm min-w-[760px]" data-testid="deliveries-table">
          <thead><tr className="text-left text-ink-soft">
            {["Date", "Type", "Public", "Destinataire", "Objet", "Statut", ""].map((h) => <th key={h} className="py-2 pr-3 font-normal">{h}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-black/5">
            {deliveries.map((d) => (
              <tr key={d.id} data-testid={`delivery-${d.id}`}>
                <td className="py-2 pr-3 whitespace-nowrap text-ink-soft">{new Date(d.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</td>
                <td className="py-2 pr-3 whitespace-nowrap">{d.kind}</td>
                <td className="py-2 pr-3">{d.audience === "interne" ? "Interne" : "Client"}</td>
                <td className="py-2 pr-3 truncate max-w-[180px]">{d.to}</td>
                <td className="py-2 pr-3 truncate max-w-[240px]">{d.subject}</td>
                <td className={`py-2 pr-3 whitespace-nowrap ${STATUS_COLORS[d.status] || ""}`}>
                  {d.status}{d.error ? <span className="block text-[0.65rem] text-bordeaux-700 truncate max-w-[160px]">{d.error}</span> : null}
                </td>
                <td className="py-2 flex gap-2 whitespace-nowrap">
                  <button data-testid={`delivery-preview-${d.id}`} onClick={() => preview(d.id)}
                    className="text-ink-soft hover:text-bordeaux-700" title="Aperçu"><Eye size={14} /></button>
                  <button data-testid={`delivery-resend-${d.id}`} onClick={() => resend(d.id)} className="text-ink-soft hover:text-bordeaux-700" title="Renvoyer"><Send size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {deliveries.length === 0 && <div className="text-sm text-ink-soft py-6">Aucun envoi pour le moment.</div>}
      </div>
    </div>
  );
}
